import { motion } from 'motion/react';

import { Button } from '@/components/ui/button';
import { ACRONYM } from '@/lib/constants';
import { sectionNumber } from '@/lib/utils';

interface HeroProps {
  onPrimaryClick?: () => void;
  /** Button copy; defaults to the signed-out call to action. */
  primaryLabel?: string;
}

/**
 * Landing hero. Editorial-scientific aesthetic:
 *   - Marginalia section number in ochre Geist Mono (§01)
 *   - Display serif in italic for the lead line
 *   - Pull-quote treatment of the acronym expansion
 *   - A single hairline rule that draws in on mount
 * No stock hero image. The typography carries the page.
 */
export function Hero({ onPrimaryClick, primaryLabel = 'Sign in to begin' }: HeroProps) {
  return (
    <section className="relative pt-16 pb-24 md:pt-24 md:pb-32">
      <div className="grid grid-cols-12 gap-6">
        {/* Marginalia column */}
        <div className="col-span-12 md:col-span-2">
          <motion.p
            className="marginalia"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <span className="mr-2">{sectionNumber(1)}</span>
            the thesis
          </motion.p>
        </div>

        {/* Main column */}
        <div className="col-span-12 md:col-span-10">
          <motion.h1
            className="font-serif text-display-xl text-ink"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            The best interfaces are not{' '}
            <span className="italic text-graphite">written</span>.
            <br />
            They are{' '}
            <span className="italic text-ochre">reasoned</span> into being.
          </motion.h1>

          {/* Hairline rule that draws in */}
          <motion.div
            className="mt-10 h-px bg-ink/30 origin-left"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.76, 0, 0.24, 1] }}
          />

          <motion.div
            className="mt-10 grid gap-8 md:grid-cols-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className="md:col-span-7">
              <p className="font-serif text-xl leading-relaxed text-ink md:text-2xl">
                AURA turns a lo-fi sketch and a short questionnaire into a
                high-fidelity website and a deployable React codebase, with{' '}
                <span className="italic text-ochre">UX laws treated as
                first-class constraints</span> rather than afterthoughts.
              </p>
              <p className="mt-6 text-base leading-relaxed text-graphite">
                Commercial vibe-coding tools and designer assistants produce
                interfaces that look finished but silently violate the
                conventions real users rely on. AURA encodes those laws,
                heuristics, and component patterns into a knowledge graph,
                and runs a pipeline of specialised agents that each consult
                it — with a dedicated validator agent that sends revisions
                back when violations are found.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button size="lg" onClick={onPrimaryClick}>
                  {primaryLabel}
                </Button>
                <a
                  href="#how-it-works"
                  className="font-mono text-xs text-graphite underline decoration-graphite/30 underline-offset-4 transition-colors hover:decoration-ochre hover:text-ochre"
                >
                  Read how it works &rarr;
                </a>
              </div>
            </div>

            {/* Side panel — acronym explanation as pull quote */}
            <aside className="md:col-span-5 md:border-l md:border-ink/15 md:pl-6">
              <p className="smallcaps text-graphite">AURA</p>
              <p className="mt-2 font-serif text-lg italic leading-snug text-ink">
                “{ACRONYM}.”
              </p>
              <div className="mt-5 space-y-1.5 font-mono text-xs text-graphite">
                <p>
                  <span className="text-ochre">¶</span>&nbsp;&nbsp;Seven
                  specialised agents.
                </p>
                <p>
                  <span className="text-ochre">¶</span>&nbsp;&nbsp;One validator
                  that critiques them.
                </p>
                <p>
                  <span className="text-ochre">¶</span>&nbsp;&nbsp;Four
                  human-in-the-loop checkpoints.
                </p>
                <p>
                  <span className="text-ochre">¶</span>&nbsp;&nbsp;Three
                  agentic revision loops.
                </p>
              </div>
            </aside>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
